import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const TOKEN_KEY = "radiants_access_token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
export function setToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer header as fallback for iframe/3rd-party-cookie blocked contexts
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Jun-2026 — force logout when the session dies mid-use (e.g. password was
// changed by an admin): any authenticated 401 outside the auth endpoints
// clears the token and sends the user to /login with a reason banner.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    if (status === 401 && !url.includes("/auth/login") && getToken()) {
      const detail = String(error?.response?.data?.detail || "");
      try {
        if (/password was changed/i.test(detail)) {
          sessionStorage.setItem("radiants_pw_changed", "1");
        }
      } catch {}
      setToken("");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatINR(value) {
  if (value === null || value === undefined || isNaN(value)) return "₹ 0";
  const num = Number(value);
  const isNeg = num < 0;
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return `${isNeg ? "-" : ""}₹ ${formatted}`;
}

export function formatDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

export function downloadFile(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function downloadReport(report, fmt, opts = {}) {
  const params = new URLSearchParams();
  if (opts.from_date) params.set("from_date", opts.from_date);
  if (opts.to_date) params.set("to_date", opts.to_date);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await api.get(`/export/${report}.${fmt}${qs}`, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(res.data);
  const stamp = (opts.from_date || opts.to_date) ? `-${opts.from_date || "all"}_to_${opts.to_date || "now"}` : "";
  downloadFile(blobUrl, `${report}${stamp}.${fmt}`);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

/** Fetch the next document number preview for a module. Returns "" if unavailable. */
export async function previewNumber(module, refDate) {
  try {
    const params = refDate ? `?ref_date=${encodeURIComponent(refDate)}` : "";
    const { data } = await api.get(`/numbering/preview/${module}${params}`);
    return data?.preview || "";
  } catch { return ""; }
}

/** Reports for which the backend ships an unbounded chunked-async exporter.
 * Must stay in-sync with `routes/async_exports.py::REPORT_DEFS`. */
export const ASYNC_EXPORT_REPORTS = new Set([
  "sales-invoices", "purchase-invoices", "general-ledger",
  "tds-ledger", "payments",
]);

/** Kick off an async export job and poll until ready, then download. The
 * caller passes `onProgress({status, processed, total})` to drive a spinner. */
export async function downloadReportAsync(report, opts = {}) {
  const params = new URLSearchParams();
  if (opts.from_date) params.set("from_date", opts.from_date);
  if (opts.to_date) params.set("to_date", opts.to_date);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const { data: started } = await api.post(`/exports/async/${report}${qs}`);
  const jobId = started.job_id;
  // Poll every 2s up to 5 minutes
  const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;
  const startedAt = Date.now();
  let job = started;
  while (Date.now() - startedAt < 5 * 60 * 1000) {
    await new Promise((r) => setTimeout(r, 2000));
    const { data } = await api.get(`/exports/async/${jobId}`);
    job = data;
    if (onProgress) onProgress(job);
    if (job.status === "ready") break;
    if (job.status === "failed") {
      throw new Error(job.error || "Async export failed");
    }
  }
  if (job.status !== "ready") {
    throw new Error("Async export timed out after 5 minutes — please refine your filters.");
  }
  const res = await api.get(`/exports/async/${jobId}/download`, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(res.data);
  const stamp = (opts.from_date || opts.to_date) ? `-${opts.from_date || "all"}_to_${opts.to_date || "now"}` : "";
  downloadFile(blobUrl, `${report}-async${stamp}.xlsx`);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  return job;
}
